// src/pages/Item.jsx
//
// One unit. A sold, hidden or unknown slug all come back as 404 from the API,
// so they share one "no longer listed" state rather than telling a visitor
// anything about stock that is not for sale.

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowLeft, Phone, Share2 } from "lucide-react";
import { getItem } from "../api";
import { useContent } from "../ContentContext";
import { Eyebrow, useTitle } from "../components/Bits";
import { Magnetic } from "../components/Effects";
import ItemGallery from "../components/ItemGallery";
import EnquiryForm from "../components/EnquiryForm";
import { makeModel, priceLabel, specRows } from "../lib/format";

export default function Item() {
  const { slug } = useParams();
  const [item, setItem] = useState(undefined); // undefined loading, null gone
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setItem(undefined);
    setError(false);
    getItem(slug)
      .then((d) => alive && setItem(d))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  useTitle(item ? `${item.title} - Brooke Requip` : "Brooke Requip");

  if (error) return <Gone title="This unit did not load" body="Refresh the page, or go back to the full list." />;
  if (item === null) {
    return (
      <Gone
        title="This unit is no longer listed"
        body="It has most likely been sold. Everything still available is on the main list."
      />
    );
  }
  if (item === undefined) return <Loading />;

  return (
    <>
      <section className="bk-wash relative">
        <div className="bk-panel-lines pointer-events-none absolute inset-0 opacity-30" />

        <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-24 sm:px-8 sm:pb-20 sm:pt-32">
          <Back />

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mt-6 grid gap-10 lg:grid-cols-[1.25fr_1fr] lg:gap-14"
          >
            <ItemGallery images={item.images} title={item.title} />
            <Details item={item} />
          </motion.div>

          {item.description && (
            <div className="mt-14 max-w-3xl border-t border-line pt-8">
              <Eyebrow>About this unit</Eyebrow>
              <p className="mt-4 whitespace-pre-line text-[15.5px] leading-relaxed text-muted">
                {item.description}
              </p>
            </div>
          )}
        </div>
      </section>

      <EnquiryForm item={item} />
    </>
  );
}

// ---------------------------------------------------------------------------
function Details({ item }) {
  const { contact } = useContent();
  const specs = specRows(item.specs);
  const mm = makeModel(item);

  return (
    <div>
      {item.category && <Eyebrow>{item.category}</Eyebrow>}
      <h1 className="mt-3 font-arch text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
        {item.title}
      </h1>
      {mm && (
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-subtle">{mm}</p>
      )}

      <p className="mt-6 font-mono text-3xl font-bold tabular-nums text-led">{priceLabel(item)}</p>

      {item.condition_note && (
        <div className="mt-6 rounded-xl border border-line bg-surface p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">Condition</p>
          <p className="mt-1.5 text-[14.5px] leading-relaxed text-ink">{item.condition_note}</p>
        </div>
      )}

      {specs.length > 0 && (
        <dl className="mt-6 border-t border-line">
          {specs.map((s) => (
            <div
              key={s.label}
              className="flex items-baseline justify-between gap-6 border-b border-line py-3"
            >
              <dt className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-subtle">
                {s.label}
              </dt>
              <dd className="text-right font-mono text-[12.5px] text-muted">{s.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-8 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
        <Magnetic className="col-span-2 sm:col-span-1">
          <a
            href="#enquire"
            className="inline-flex min-h-[50px] w-full items-center justify-center rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark"
          >
            Ask about this unit
          </a>
        </Magnetic>
        <a
          href={`tel:${contact.phone}`}
          className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-full border border-line px-5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted transition hover:border-brooke-red hover:text-ink"
        >
          <Phone size={14} />
          Call
        </a>
        <ShareButton item={item} />
      </div>
    </div>
  );
}

// The phone's own share sheet where there is one, WhatsApp otherwise - which
// is how a listing actually gets passed around between business owners here.
function ShareButton({ item }) {
  const share = async () => {
    const url = window.location.href;
    const text = `${item.title} - ${priceLabel(item)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: item.title, text, url });
      } catch {
        // cancelled, nothing to do
      }
      return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank", "noopener");
  };

  return (
    <button
      type="button"
      onClick={share}
      className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-full border border-line px-5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted transition hover:border-brooke-red hover:text-ink"
    >
      <Share2 size={14} />
      Share
    </button>
  );
}

// ---------------------------------------------------------------------------
function Back() {
  return (
    <Link
      to="/"
      className="inline-flex min-h-[40px] items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle transition hover:text-brooke-red"
    >
      <ArrowLeft size={13} />
      All stock
    </Link>
  );
}

function Loading() {
  return (
    <section className="mx-auto max-w-6xl px-5 pb-20 pt-24 sm:px-8 sm:pt-32">
      <div className="h-10 w-28 animate-pulse rounded bg-surface2" />
      <div className="mt-6 grid gap-10 lg:grid-cols-[1.25fr_1fr] lg:gap-14">
        <div className="aspect-[4/3] animate-pulse rounded-2xl bg-surface2" />
        <div className="space-y-4">
          <div className="h-4 w-24 animate-pulse rounded bg-surface2" />
          <div className="h-10 w-3/4 animate-pulse rounded bg-surface2" />
          <div className="h-8 w-40 animate-pulse rounded bg-surface2" />
          <div className="h-32 w-full animate-pulse rounded-xl bg-surface2" />
        </div>
      </div>
    </section>
  );
}

function Gone({ title, body }) {
  return (
    <section className="bk-wash relative min-h-[70vh]">
      <div className="bk-panel-lines pointer-events-none absolute inset-0 opacity-30" />
      <div className="relative mx-auto max-w-6xl px-5 pb-20 pt-32 sm:px-8 sm:pt-40">
        <Eyebrow>Requip</Eyebrow>
        <h1 className="mt-4 max-w-[20ch] font-arch text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
          {title}
        </h1>
        <p className="mt-5 max-w-[46ch] text-[16px] leading-relaxed text-muted">{body}</p>
        <Link
          to="/"
          className="mt-8 inline-flex min-h-[50px] items-center justify-center rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition hover:bg-brooke-redDark"
        >
          See what is in stock
        </Link>
      </div>
    </section>
  );
}
